// English texts for "What fits our family" (see profileApp.nl.ts). The consent texts belong to
// PROFILE_CONSENT_VERSION and CONSENT_TEXT_HASHES (packages/domain/src/profile/vocab.ts).
export const appProfileEn = {
    title: 'What fits our family',
    intro: 'Choose what fits you, and the lists get calmer. Everything is optional.',
    privacyLine: 'Only parents can view and change this. Linked family never sees it.',
    privacyLink: 'Privacy statement',
    privacyPage: {
        title: 'Privacy statement',
        back: 'Back to the app',
        intro: 'About "What fits our family": cuisine, faith, food and special days. Short and clear.',
        sections: [
            {
                title: 'What for',
                body: 'We only use your choices to decide which options you see: templates, pictograms, recipes and special days. Not for advertising, not to build profiles about you and not to share.',
            },
            {
                title: 'Which data',
                body: 'Cuisines and traditions, faith (with the practices and the madhab you chose), food rules (such as halal or vegetarian), differences per family member, "eats something else" for a recipe and the special days you switched on. Faith, halal and kosher, and sometimes a tradition, are special categories of personal data (GDPR article 9).',
            },
            {
                title: 'Legal basis',
                body: 'Your explicit consent (GDPR article 9(2)(a)). A parent gives it for themselves and for children under 16; an adult with their own login can give consent for their own differences. Everything is optional: without consent the app works as usual.',
            },
            {
                title: 'Who sees it',
                body: 'Only the parents in your family see the choices themselves. The tablet and the app only show what fits; for food we name who doesn’t eat something. Linked family, guests, share links, calendar feeds, error reports and log files never get the choices. Special days only go into a calendar export if you switch that on separately.',
            },
            {
                title: 'How long',
                body: 'As long as the consent is valid. Withdrawing deletes the choices immediately; they disappear from backups within 90 days. We keep the record of the consent itself (who, when, which text, no content) for up to 5 years after withdrawal, or until the family is deleted.',
            },
            {
                title: 'Your rights',
                body: 'You can view and change your data in Settings · What fits our family, download it in Settings · Download data (every parent their own data, the owner everything), withdraw your consent (which deletes everything) and ask for erasure. If you disagree with something, you can file a complaint with the Dutch Data Protection Authority (Autoriteit Persoonsgegevens).',
            },
            {
                title: 'Controller and contact',
                body: 'AMUMIN B.V. is responsible for this data. You can download your own data yourself in Settings. For questions or erasure, contact AMUMIN B.V.; until the dedicated privacy address below is listed, through the administrator of your family account. A dedicated privacy address and the Chamber of Commerce details will be listed here before other families can sign up.',
            },
        ],
    },
    settingsBody: 'Cuisine, faith, food and special days',
    saveBar: 'Save changes',
    invite: {
        title: 'What fits your family?',
        body: 'Choose cuisine, faith and food to mostly see what fits you. You don’t have to.',
        start: 'Set up',
        later: 'Not now',
        never: 'No, thanks',
    },
    otherParent: {
        title: 'What fits your family',
        body: '{name} set up what fits your family: {what}. Does this fit you too?',
        titleNeutral: 'What fits you all',
        bodyNeutral: '{name} set up what fits you all: {what}. Does this fit you too?',
        view: 'View',
    },
    wizard: {
        title: 'What fits you?',
        body: 'Only choose what you want; the rest can stay empty. You can change everything later.',
        skip: 'Skip',
        skipQuestion: 'Skip this question',
        next: 'Next',
        done: 'Done',
        save: 'Save',
        nothingSaved: 'You didn’t choose anything, so we save nothing.',
        step: '{n} of {total}',
        question: '{step} · question {n} of {total}',
    },
    traditions: {
        title: 'Cuisine and traditions',
        body: 'Which cuisines and traditions belong to your family? Feel free to pick more than one.',
        hint: 'This is about customs, not nationality. We only remember which cuisines you like. We use it to order recipes and to suggest special days.',
        labels: {
            dutch: 'Dutch',
            turkish: 'Turkish',
            moroccan: 'Moroccan',
            surinamese: 'Surinamese',
            antillean: 'Antillean',
            indonesian: 'Indo / Indonesian',
            moluccan: 'Moluccan',
            italian: 'Italian',
            polish: 'Polish',
            middle_eastern: 'Middle Eastern',
            other: 'Other',
        },
        otherKitchens: {
            title: 'Which cuisines do you like?',
            more: 'More cuisines',
        },
    },
    suggestion: {
        title: 'Does this fit you?',
        body: 'You decide. You can always change it.',
        accept: 'Yes, use this',
        decline: 'I’ll choose',
        days: 'There are also {sets} you can add. You switch them on yourself under Special days.',
    },
    faiths: {
        title: 'Faith',
        body: 'You’ll see the matching options. You can switch on holidays yourself afterwards.',
        none: 'No faith',
        shows: 'You’ll see {list}',
        /** Golf 6b: gewoontes bij het gekozen geloof, optioneel (niets voorgevinkt). */
        madhab: {
            title: 'Which madhab (school of law) do you follow?',
            body: 'Among other things, this helps us set the time of asr (ikindi) correctly. You can always change it.',
            hint: 'Turkish mosques, such as those of Diyanet and Milli Görüş, follow the Hanafi school. Moroccan mosques usually follow the Maliki school.',
            unknown: 'Don’t know / skip',
            labels: { hanafi: 'Hanafi', shafii: 'Shafi’i', maliki: 'Maliki', hanbali: 'Hanbali' },
        },
        practicesTitle: 'What is part of your life?',
        practicesHint: 'Tick what you do. Only that is set up as an appointment, chore or picture. Ticking nothing is fine too.',
        openHint: 'If you choose nothing, Prayer and Mosque lessons are not among the suggestions (search still finds them). Choose your faith if you want to see them.',
        labels: {
            islam: 'Islam',
            christianity: 'Christianity',
            judaism: 'Judaism',
            hinduism: 'Hinduism',
            other: 'Another faith',
        },
    },
    food: {
        title: 'Food',
        body: 'Recipes and groceries that don’t fit are moved out of sight. Search or "Show all" always finds them.',
        labels: {
            halal: 'Halal',
            no_pork: 'No pork',
            no_beef: 'No beef',
            vegetarian: 'Vegetarian',
            vegan: 'Vegan',
            kosher: 'Kosher',
            no_alcohol: 'No alcohol',
        },
    },
    days: {
        title: 'Special days in the calendar',
        optional: 'optional',
        body: 'Want extra days in the calendar? Dutch public holidays are on; the extra sets are all off. Switch on what you want to see.',
        perDay: 'Choose per day',
        more: 'More special days',
        next: 'Next: {dates}',
        noDates: 'Dates for this year will follow.',
        expected: '(expected)',
        expectedNote: 'The exact day may shift by one day.',
        eveBefore: 'Starts the evening before at sunset.',
        eitherOr: '{a} or {b}',
        expectedTag: 'expected',
        sheet: {
            expected: 'This day is not fixed yet.',
            when: 'Falls on {dates}.',
            confirm: 'Confirm the day',
            confirmHint: 'Confirm or move it under What fits our family, Special days.',
        },
        nl: {
            title: 'Dutch public holidays',
            note: 'For everyone. On by default.',
            days: {
                nieuwjaarsdag: 'New Year’s Day',
                goede_vrijdag: 'Good Friday',
                pasen: 'Easter',
                koningsdag: 'King’s Day',
                bevrijdingsdag: 'Liberation Day',
                hemelvaartsdag: 'Ascension Day',
                pinksteren: 'Whitsun',
                kerstmis: 'Christmas',
                moederdag: 'Mother’s Day',
                vaderdag: 'Father’s Day',
                sinterklaas: 'Sinterklaas',
                oudejaarsavond: 'New Year’s Eve',
            },
        },
        dayNames: {
            ramadan_start: 'Start of Ramadan',
            ramadan_end: 'Last day of Ramadan',
            eid_al_fitr: 'Eid al-Fitr',
            eid_al_adha: 'Eid al-Adha',
            islamic_new_year: 'Islamic New Year',
            mawlid: 'Mawlid',
        },
        tradition: {
            title: 'Which calendar do you follow?',
            body: 'Mosques do not all set the holidays in the same way.',
            options: {
                diyanet: 'Turkish (Diyanet, fixed schedule)',
                saudi: 'Saudi Arabia (moon sighting)',
                morocco: 'Morocco (moon sighting, confirmed the evening before)',
                own_mosque: 'Our own mosque (±1 day)',
            },
            hints: {
                diyanet: 'The dates are fixed years ahead.',
                saudi: 'Until the announcement the evening before, you see two possible days.',
                morocco: 'Often one day later than in Saudi Arabia. Until the announcement, you see two possible days.',
                own_mosque: 'You see the fixed schedule. If your mosque picks a day earlier or later, set it here.',
            },
            defaultNote: 'By default the fixed Diyanet table. Feel free to choose what your mosque follows.',
            confirmTitle: 'Announced yet?',
            confirmBody: 'Tap the day your mosque chose. It is then fixed in the calendar.',
            unknown: 'Not known yet',
            confirmedNote: 'Fixed in the calendar.',
        },
        sets: {
            tr_national: 'Turkish national days',
            keti_koti: 'Keti Koti',
            indies_remembrance: 'Remembrance 15 August',
            id_independence: 'Indonesian Independence Day',
            it_national: 'Italian national days',
            pl_national: 'Polish national days',
            islamic: 'Islamic holidays',
            christian: 'Christian days',
            jewish: 'Jewish holidays',
            hindu: 'Hindu holidays',
        },
        ics: {
            toggle: 'Also include special days in calendar exports',
            warning: 'Your calendar app (for example Google) will then see these days.',
        },
    },
    flags: {
        pork: 'pork',
        beef: 'beef',
        meat: 'meat',
        fish: 'fish',
        shellfish: 'shellfish',
        dairy: 'dairy',
        egg: 'egg',
        honey: 'honey',
        gelatin: 'gelatine',
        alcohol: 'alcohol',
    },
    kitchens: {
        dutch: 'Dutch',
        turkish: 'Turkish',
        moroccan: 'Moroccan',
        surinamese: 'Surinamese',
        antillean: 'Antillean',
        indonesian: 'Indonesian',
        moluccan: 'Moluccan',
        italian: 'Italian',
        polish: 'Polish',
        middle_eastern: 'Middle Eastern',
        chinese: 'Chinese',
        indian: 'Indian',
        mexican: 'Mexican',
        greek: 'Greek',
        west_african: 'West African',
        central_african: 'Central African',
        east_african: 'East African',
        american: 'American',
    },
    member: {
        follows: 'Follows the family',
        differs: 'Different from the family',
        reset: 'Follow the family again',
        plus: 'Follows the family, plus {what}',
        minus: 'Follows the family, without {what}',
        faithDiffers: 'Faith: {what}',
        notPossible: 'You can’t set this separately for {name}. That is only possible if {name} gives consent with their own login. Until then {name} follows the family.',
        askSelf: 'Ask {name} to set this up themselves.',
        shareLink: 'Share link',
    },
    consent: {
        version: '2026-09-25.4',
        title: 'Is this OK?',
        text: 'Faith, food rules and cuisines can be sensitive data. We only use them to choose which options you see.\nOnly parents can view and change this. The tablet and the app show what fits; for food we name who doesn’t eat something.\nSome choices let you put extra parts on the board. Everyone in the room then sees them.\nWe share nothing with linked family or in shared lists. Special days only appear in the calendar or in a calendar export if you switch that on yourself.\nYou can withdraw at any time and that deletes it immediately. It disappears from backups within 90 days.\nAMUMIN B.V. is responsible for this data. Your rights are in the privacy statement.',
        check: 'I give consent for myself and for my children under 16.',
        given: 'Consent given by {name} on {date}',
        /** Fijnslijpen: zonder bekende naam (geen "door  op"). */
        givenAt: 'Consent given on {date}',
        renew: 'The explanation has changed. Please read it and give consent again.',
        withdraw: 'Withdraw consent and delete',
        withdrawConfirm: 'We will delete your family’s cuisines, faith, food rules and special days. You will see everything again. Continue?',
        withdrawn: 'Deleted. You see everything again.',
    },
    selfConsent: {
        title: 'Does this fit you?',
        text: 'You set what you believe or eat for yourself. We only use it to choose which options you see, and for food we name you when something doesn’t fit you. Only you and the parents in this family see it. Withdrawing deletes it immediately.',
        check: 'I give consent for myself.',
        withdraw: 'Withdraw my consent',
    },
    daySetsInline: {
        tr_national: 'Turkish holidays',
        keti_koti: 'Keti Koti',
        indies_remembrance: 'the 15 August remembrance',
        id_independence: 'Indonesian Independence Day',
        it_national: 'Italian holidays',
        pl_national: 'Polish holidays',
        islamic: 'Islamic holidays',
        christian: 'Christian days',
        jewish: 'Jewish holidays',
        hindu: 'Hindu holidays',
    },
    hidden: {
        showAll: 'Show all',
        showLess: 'Show less',
        count: { one: '{count} option hidden', other: '{count} options hidden' },
        groupFood: 'Hidden because you {rules}',
        groupFaith: 'Belongs to another faith',
        groupNotChosen: 'Appears when you choose {what}',
        groupFaithOpen: 'Hidden until you choose a faith',
        /** Golf 6b: geloof gekozen, gewoonte niet aangevinkt. */
        groupPractice: 'Not ticked under faith',
        practice: 'Hidden because you did not tick {practice} under faith. Change in Settings.',
        change: 'Change',
        faith: 'Belongs to {faiths}. You chose something else.',
        faithNone: 'Hidden because you chose No faith. Change in Settings.',
        notChosen: 'Appears when you choose {what} in Settings.',
        faithOpen: 'Hidden until you choose a faith. Change in Settings.',
        food: 'Hidden because you {rules}. Change in Settings.',
        variant: 'A variant of this recipe is already in the list.',
        rulePhrase: {
            halal: 'eat halal',
            no_pork: 'don’t eat pork',
            no_beef: 'don’t eat beef',
            vegetarian: 'eat vegetarian',
            vegan: 'eat vegan',
            kosher: 'eat kosher',
            no_alcohol: 'don’t use alcohol',
        },
    },
    warning: {
        contains: 'This contains {ingredient}.',
        containsMany: 'This contains {ingredient}.',
        person: {
            halal: '{names} eats halal.',
            no_pork: '{names} doesn’t eat pork.',
            no_beef: '{names} doesn’t eat beef.',
            vegetarian: '{names} eats vegetarian.',
            vegan: '{names} eats vegan.',
            kosher: '{names} eats kosher.',
            no_alcohol: '{names} doesn’t use alcohol.',
        },
        people: {
            halal: '{names} eat halal.',
            no_pork: '{names} don’t eat pork.',
            no_beef: '{names} don’t eat beef.',
            vegetarian: '{names} eat vegetarian.',
            vegan: '{names} eat vegan.',
            kosher: '{names} eat kosher.',
            no_alcohol: '{names} don’t use alcohol.',
        },
        everyone: {
            halal: 'You all eat halal.',
            no_pork: 'You all don’t eat pork.',
            no_beef: 'You all don’t eat beef.',
            vegetarian: 'You all eat vegetarian.',
            vegan: 'You all eat vegan.',
            kosher: 'You all eat kosher.',
            no_alcohol: 'You all don’t use alcohol.',
        },
        check: 'Note: this may contain {what}.',
        meatDairy: 'This has meat with dairy: {meat} and {dairy}.',
        item: {
            pork: 'This contains pork.',
            beef: 'This contains beef.',
            meat: 'This contains meat.',
            fish: 'This contains fish.',
            shellfish: 'This contains shellfish.',
            dairy: 'This contains dairy.',
            egg: 'This contains egg.',
            honey: 'This contains honey.',
            gelatin: 'This usually contains pork gelatine.',
            alcohol: 'This contains alcohol.',
        },
        swap: 'Tip: {swap} instead of {ingredient}.',
        swapButton: 'Swap',
        addAnyway: 'Keep it',
        add: 'OK',
        eatsOther: '{name} eats something else',
        whoEats: 'Who’s eating?',
        everyoneEats: 'Everyone',
        and: 'and',
    },
    screens: {
        practices: {
            prayer: 'Prayer',
            wudu: 'Wudu',
            mosque: 'Mosque (lessons)',
            quran: 'Quran lessons',
            church: 'Church',
            shabbat: 'Shabbat',
            synagogue: 'Synagogue',
            mandir: 'Mandir',
        },
        noneHint: 'Then we move faith options such as Prayer and Mosque lessons out of sight.',
        otherFaithHint: 'Prayer stays visible; the options of the named faiths do not.',
        stepLabel: 'Step {n} of {total}',
        progress: 'Question {n} of {total}',
        noConsentYet: 'Without permission we don’t save anything.',
        saved: 'Saved. You’ll mostly see what suits you.',
        savedMember: 'Saved for {name}.',
        resetMember: '{name} follows the family again.',
        savedSelf: 'Saved.',
        resetSelf: 'You follow the family again.',
        selfWithdrawn: 'Your own choices have been erased.',
        noChanges: 'Nothing has changed.',
        loadError: 'This could not be loaded.',
        changed: 'You changed something.',
        settingsCardBody: 'Kitchen, faith, food and special days',
        membersTitle: 'Per family member',
        membersIntro: 'The family is the default. A family member can differ.',
        consentTitle: 'Permission',
        appliesTo: 'Applies to: {list}',
        selfTag: '{name} (themselves)',
        kidsTag: '{list} (children under 16)',
        noConsent: 'No permission has been given yet. We ask for it the first time you save.',
        inviteLater: 'We’ll ask again later.',
        inviteNever: 'Fine. You can always find it in Settings.',
        otherParentWhat: 'kitchen, faith and food',
        dayCount: 'day {n} of {total}',
        daysOn: 'On',
        daysOff: 'Off',
        lessDays: 'Fewer special days',
        offeredEmpty: 'Choose kitchen or faith above, and we’ll suggest matching days.',
        member: {
            title: 'Adjust',
            open: 'Adjust for {name}',
            faith: 'Faith',
            food: 'Food',
            foodHint: 'The family’s choices are ticked. What you switch on is added, what you switch off doesn’t apply to {name}. Later family choices follow automatically.',
            faithFollows: 'Faith: like the family',
            lapsed: '{name} now sets this themselves.',
            selfIntro: 'You set this for yourself.',
            selfGiven: 'You gave permission for yourself.',
            withdrawSelfTitle: 'Erase your own choice?',
            withdrawSelfBody: 'You will follow the family again. What you set for yourself is erased immediately.',
            withdrawSelfConfirm: 'Erase',
            save: 'Save',
            linkCopied: 'Link copied.',
            notSetYet: 'First set what suits the family and save it. After that you can differ per family member.',
        },
        self: {
            title: 'What suits me',
            intro: 'Do you believe or eat differently from the family? Set it here yourself. Only you and your parents can see it.',
            parents: 'Your parents set this. From age 16 you can set here what is different for you.',
            notYet: 'Your parents have not set what suits your family yet. After that you can make your own choices here.',
            settingsBody: 'Your own faith and food',
        },
    },
};
//# sourceMappingURL=profileApp.en.js.map